import { grey } from '@mui/material/colors';

type defaultPaletteType = {
	main: string;
	dark: string;
	light: string;
	contrastText: string;
	containedHoverBackground: string;
	outlinedHoverBackground: string;
	outlinedRestingBackground: string;
};
type extendedPaletteType = Omit<
	defaultPaletteType,
	'outlinedRestingBackground'
> & {
	content: string;
	background: string;
	outlineRestingBorder: string;
};

type textPaletteType = {
	primary: string;
	secondary: string;
	disabled: string;
};

type actionPaletteType = {
	active: string;
	hover: string;
	selected: string;
	disabled: string;
	disabledBackground: string;
	focus: string;
};

type otherPaletteType = {
	divider: string;
	outlineBorder: string;
	standardInputLine?: string;
	backdropOverlay: string;
	ratingActive: string;
	snackbarBackground: string;
	active?: string;
	backgroundResolvedMessage?: string;
	backgroundDefault: string;
	backgroundPaper: string;
	backgroundPaperAlternative: string;
	backgroundSideMenu: string;
	backgroundSideMenuItemLight: string;
	backgroundSideMenuItemDark: string;
	tableBackground: string;
	tableBorder: string;
};

type modePaletteType = {
	primary: defaultPaletteType;
	error: extendedPaletteType;
	warning: extendedPaletteType;
	success: extendedPaletteType;
	info: extendedPaletteType;
	text: textPaletteType;
	action: actionPaletteType;
	other: otherPaletteType;
};

type modeType = {
	palette: modePaletteType;
};

type paletteType = {
	light: modeType;
	dark: modeType;
};

export const customPalette: paletteType = {
	light: {
		palette: {
			primary: {
				main: '#0091EA',
				dark: '#0277BD',
				light: '#4FC3F7',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#1165B2',
				outlinedHoverBackground: '#EDF0F8',
				outlinedRestingBackground: '#9FA8DA',
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#F88078',
				contrastText: '#C4C4C4',
				containedHoverBackground: '#AB2F26',
				outlinedHoverBackground: '#FEF0EF',
				outlineRestingBorder: '#F9A19A',
				content: '#621B16',
				background: '#FEECEB',
			},
			warning: {
				main: '#ED6C02',
				dark: '#C77700',
				light: '#FFB547',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#A64C01',
				outlinedHoverBackground: '#FEF3EB',
				outlineRestingBorder: '#F6B580',
				content: '#A64C01',
				background: '#FDF0E6',
			},
			success: {
				main: '#4CAF50',
				dark: '#3B873E',
				light: '#7BC67E',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#357B38',
				outlinedHoverBackground: '#F1F9F1',
				outlineRestingBorder: '#A5D7A7',
				content: '#1E4620',
				background: '#EDF7ED',
			},
			info: {
				main: '#2196F3',
				dark: '#0B79D0',
				light: '#64B6F7',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#1769AA',
				outlinedHoverBackground: '#EDF7FE',
				outlineRestingBorder: '#90CBF9',
				content: '#0D3C61',
				background: '#E9F4FE',
			},
			text: {
				primary: '#212121',
				secondary: '#666666',
				disabled: '#9E9E9E',
			},
			action: {
				active: '#757575',
				hover: '#F5F5F5',
				selected: '#EBEBEB',
				disabled: '#BDBDBD',
				disabledBackground: '#E0E0E0',
				focus: '#E0E0E0',
			},
			other: {
				divider: '#E0E0E0',
				outlineBorder: '#C4C4C4',
				standardInputLine: '#949494',
				backdropOverlay: '#808080',
				ratingActive: '#FFB400',
				snackbarBackground: '#323232',
				backgroundDefault: '#FAFAFA',
				backgroundPaper: '#FFFFFF',
				backgroundPaperAlternative: '#F5F5F5',
				backgroundSideMenu: '#F5F5F5',
				backgroundSideMenuItemLight: '#FFFFFF',
				backgroundSideMenuItemDark: grey[100],
				tableBackground: '#F5F5F5',
				tableBorder: '#E0E0E0',
			},
		},
	},
	dark: {
		palette: {
			primary: {
				main: '#4EBCFF',
				dark: '#094B73',
				light: '#A6D4FA',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#0066A4',
				outlinedHoverBackground: '#2B2F37',
				outlinedRestingBackground: '#155E8C',
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#E57373',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#AB2F26',
				outlinedHoverBackground: '#3A2D30',
				outlineRestingBorder: '#8F3733',
				content: '#FBB4AF',
				background: '#2E0C0A',
			},
			warning: {
				main: '#FFA726',
				dark: '#F57C00',
				light: '#FFB74D',
				contrastText: '#050606',
				containedHoverBackground: '#B2751B',
				outlinedHoverBackground: '#94692B',
				outlineRestingBorder: '#3B352E',
				content: '#FFDCA8',
				background: '#301E04',
			},
			success: {
				main: '#66BB6A',
				dark: '#388E3C',
				light: '#81C784',
				contrastText: '#FFFFFF',
				containedHoverBackground: '#47834A',
				outlinedHoverBackground: '#2F3734',
				outlineRestingBorder: '#48734D',
				content: '#C2E4C3',
				background: '#0A130B',
			},
			info: {
				main: '#29B6F6',
				dark: '#0288D1',
				light: '#4FC3F7',
				contrastText: '#050606',
				containedHoverBackground: '#1D7FAC',
				outlinedHoverBackground: '#2A7192',
				outlineRestingBorder: '#2A363F',
				content: '#A9E2FB',
				background: '#071F2E',
			},
			text: {
				primary: '#FFFFFF',
				secondary: '#8690A0',
				disabled: '#949597',
			},
			action: {
				active: '#8690A0',
				hover: '#6A6B6D',
				selected: '#4C4D50',
				disabled: '#6A6B6D',
				disabledBackground: '#444448',
				focus: '#444448',
			},
			other: {
				divider: '#585E68',
				outlineBorder: '#596472',
				backdropOverlay: '#151618',
				ratingActive: '#FFB400',
				snackbarBackground: '#FFFFFF',
				active: '#84E188',
				backgroundResolvedMessage: '#334138',
				backgroundDefault: '#1A1B1D',
				backgroundPaper: '#2A2B2F',
				backgroundPaperAlternative: '#2A2B2F',
				backgroundSideMenu: '#2A2B2F',
				backgroundSideMenuItemLight: '#393a3d',
				backgroundSideMenuItemDark: '#2A2B2F',
				tableBackground: '#333333',
				tableBorder: '#2A2B2F',
			},
		},
	},
};
